import React, { useState, useEffect } from 'react';
import { urls } from './config.dev';
import { Link, useNavigate } from 'react-router-dom';
import Topbar from './Topbar';
import MissingDrugs from './MissingDrugs';
import ReceiptModal from './ReceiptModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faCheck, faTimes, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import 'font-awesome/css/font-awesome.min.css';
import './selldrugs.css';

function Selldrugs() {
  // State management (same as before)
  const [stockData, setStockData] = useState([]);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [quantityToSell, setQuantityToSell] = useState(1);
  const [sellItems, setSellItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showTransactionPrompt, setShowTransactionPrompt] = useState(false);
  const [employeeName, setEmployeeName] = useState('');
  const [clinicName, setClinicName] = useState('');
  const [isSellButtonActive, setIsSellButtonActive] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [confirmClicked, setConfirmClicked] = useState(false);
  const [confirmButtonText, setConfirmButtonText] = useState('Confirm Transaction');
  const [searchQuery, setSearchQuery] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState({
    clinicName: '',
    district: '',
    town: '',
    ownersContact: '',
    drugsSold: [],
    totalAmount: 0,
    employeeName: '',
    date: '',
    time: '',
  });

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get('token');

    // Security check function
  const performSecurityCheck = async (token) => {
    try {
      const securityResponse = await fetch(urls.security, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (securityResponse.ok) {
        const securityData = await securityResponse.json();
        if (securityData.message === 'Session valid') {
          setEmployeeName(securityData.employee_name);
          setClinicName(securityData.clinic);
          return true;
        } else if (securityData.error === 'Session expired') {
          navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          return false;
        } else {
          navigate('/login');
          return false;
        }
      }
      throw new Error('Failed to perform security check');
    } catch (error) {
      console.error('Error performing security check:', error);
      navigate('/login');
      return false;
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    const fetchDispensaryStock = async (token) => {
      try {
        const response = await fetch(urls.fetchdispensary, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await response.json();
        setStockData(data);
      } catch (error) {
        console.error('Error fetching shelves stock:', error);
      }
    };

    const fetchTokenAndCheckSecurity = async () => {
      try {
        if (!tokenFromUrl) throw new Error('Token not found in URL');
        const securityPassed = await performSecurityCheck(tokenFromUrl);
        if (securityPassed) {
          await fetchDispensaryStock(tokenFromUrl);
          const intervalId = setInterval(() => fetchDispensaryStock(tokenFromUrl), 5000);
          return () => clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Error:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate, tokenFromUrl]);

  // Drug selection handler
  const handleDrugSelect = (drug) => {
    setSelectedDrug(drug);
    setQuantityToSell(1);
    setSearchQuery('');
    setIsSellButtonActive(false);
  };

  // Remove item from cart
  const handleRemoveSellItem = (index) => {
    const updatedSellItems = [...sellItems];
    const removedItem = updatedSellItems.splice(index, 1)[0];
    setSellItems(updatedSellItems);
    setTotalAmount(totalAmount - removedItem.amount);
    setIsSellButtonActive(updatedSellItems.length > 0);
  };

  // Transaction handlers
  const handleConfirmTransaction = () => setShowTransactionPrompt(true);
  const handleCancelTransaction = () => setShowTransactionPrompt(false);

  // Confirm sale handler
  const handleConfirmSale = async () => {
    setConfirmClicked(true);
    setConfirmButtonText('Processing...');

    const currentDate = new Date().toLocaleDateString('en-UG', { timeZone: 'Africa/Kampala' });
    const currentTime = new Date().toLocaleTimeString('en-US', { timeZone: 'Africa/Kampala' });

    const saleData = {
      sales: sellItems.map(item => ({
        drug: item.drug,
        packaging: item.packaging,
        quantitySold: item.quantity,
        amount: item.amount,
        sellingPrice: item.amount / item.quantity,
      })),
      totalAmount: totalAmount,
      employeeName: employeeName,
      currentDate: currentDate,
      currentTime: currentTime,
      token: tokenFromUrl,
    };

    try {
      const response = await fetch(urls.selldrugs, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saleData),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.message === "Drugs sold successfully!") {
          const clinicResponse = await fetch(urls.fetchclinicdetails, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: tokenFromUrl })
          });

          if (clinicResponse.ok) {
            const clinicDetails = await clinicResponse.json();
            setReceiptDetails({
              clinicName: clinicDetails.clinic_name,
              district: clinicDetails.district,
              town: clinicDetails.sub_county,
              ownersContact: clinicDetails.owners_contact,
              drugsSold: sellItems.map(item => ({
                drug: item.drug,
                quantity: item.quantity,
                packaging: item.packaging,
                unitPrice: item.amount / item.quantity,
                amount: item.amount,
              })),
              totalAmount: totalAmount,
              employeeName: employeeName,
              date: currentDate,
              time: currentTime,
            });
            setShowTransactionPrompt(false);
            setTimeout(() => setShowReceiptModal(true), 300);

            // Send to average endpoint
            await fetch(urls.assignAverage, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(saleData)
            });
          }
        } else {
          setResponseMessage({
            type: "error-message",
            text: `Transaction failed. Please check stock availability and network connection.`
          });
        }
        setSellItems([]);
        setTotalAmount(0);
      } else {
        setResponseMessage({
          type: "error-message",
          text: `Transaction failed. Please check stock availability and network connection.`
        });
      }
    } finally {
      setConfirmClicked(false);
      setConfirmButtonText('Confirm Transaction');
      setTimeout(() => setResponseMessage(null), 7000);
    }
  };

  // Price and quantity handlers
  const handleUnitPriceChange = (index, value) => {
    const updatedItems = [...sellItems];
    const newPrice = parseFloat(value) || 0;
    updatedItems[index].unitPrice = newPrice;
    updatedItems[index].amount = newPrice * updatedItems[index].quantity;
    setSellItems(updatedItems);
    calculateTotalAmount(updatedItems);
  };

const handleQuantityChange = (index, newQuantity) => {
  const updatedItems = [...sellItems];
  const quantity = newQuantity === '' ? '' : Math.max(1, Number(newQuantity));
  updatedItems[index].quantity = quantity;
  updatedItems[index].amount = (quantity === '' ? 0 : quantity) * updatedItems[index].unitPrice;
  setSellItems(updatedItems);
  calculateTotalAmount(updatedItems);
};

  const handleSellButtonClick = () => {
    if (selectedDrug) {
      const quantity = quantityToSell === '' ? 1 : quantityToSell;
      const newItem = {
        drug: selectedDrug.Drug,
        packaging: selectedDrug.Packaging,
        quantity: quantity,
        unitPrice: selectedDrug.Selling_Price,
        amount: quantity * selectedDrug.Selling_Price,
      };
      setSellItems([...sellItems, newItem]);
      calculateTotalAmount([...sellItems, newItem]);
      setSelectedDrug(null);
      setQuantityToSell(1);
      setIsSellButtonActive(true);
    }
  };

  const calculateTotalAmount = (items) => {
    setTotalAmount(items.reduce((sum, item) => sum + item.amount, 0));
  };
  // ... (keep all your existing functions unchanged)

  // Updated styles for full-width and full-height utilization
  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f5f7fa',
      minHeight: '100vh',
      paddingTop: '65px',
      paddingBottom: '60px',
      margin: 0,
      width: '100%'
    },
    bottomNav: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'space-between',
      padding: '10px 15px',
      backgroundColor: '#f8f9fa',
      borderTop: '1px solid #e0e0e0',
      boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
      zIndex: 100
    },
    navButton: {
      color: '#2c3e50',
      textDecoration: 'none',
      padding: '8px 12px',
      borderRadius: '4px',
      backgroundColor: '#ffffff',
      border: '1px solid #e0e0e0',
      transition: 'all 0.3s',
      flex: 1,
      margin: '0 5px',
      textAlign: 'center'
    },
    content: {
      padding: '20px',
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      boxSizing: 'border-box'
    },
    mainLayout: {
      display: 'flex',
      flexDirection: 'row',
      gap: '20px',
      width: '100%',
      height: 'calc(100vh - 125px)',
      boxSizing: 'border-box'
    },
    cartContainer: {
      flex: 1,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      padding: '20px',
      minWidth: '450px',
      height: '100%',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column'
    },
    drugListContainer: {
      flex: 2,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      padding: '20px',
      height: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '15px'
    },
    tableHeader: {
      backgroundColor: '#f8f9fa',
      color: '#2c3e50',
      padding: '12px',
      textAlign: 'left',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    tableCell: {
      padding: '10px',
      borderBottom: '1px solid #e0e0e0',
      fontSize: '14px'
    },
    inputField: {
      width: '70px',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      textAlign: 'center',
      fontSize: '14px'
    },
    removeButton: {
      backgroundColor: 'transparent',
      color: '#e74c3c',
      border: 'none',
      borderRadius: '4px',
      padding: '5px 10px',
      cursor: 'pointer'
    },
    addButton: {
      backgroundColor: '#2ecc71',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 12px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    confirmButton: {
      backgroundColor: '#2ecc71',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '10px 15px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    totalAmount: {
      fontWeight: 'bold',
      fontSize: '16px',
      marginTop: '15px',
      color: '#2c3e50'
    },
    searchInput: {
      width: '100%',
      padding: '12px 15px 12px 40px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      marginBottom: '15px',
      fontSize: '14px'
    },
    drugRow: {
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '30px',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      maxWidth: '500px',
      width: '90%'
    },
    transactionButtons: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px',
      gap: '15px'
    },
    cancelButton: {
      backgroundColor: '#f8f9fa',
      color: '#2c3e50',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      padding: '10px 15px',
      cursor: 'pointer',
      flex: 1
    },
    successMessage: {
      color: '#2ecc71',
      margin: '10px 0',
      fontSize: '14px'
    },
    errorMessage: {
      color: '#e74c3c',
      margin: '10px 0',
      fontSize: '14px'
    },
    searchIcon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#95a5a6'
    },
    drugListContent: {
      flex: 1,
      overflowY: 'auto',
      width: '100%'
    }
  };

  return (
    <div style={styles.container}>
      <Topbar token={tokenFromUrl} />
      <MissingDrugs token={tokenFromUrl} />
      
      <div style={styles.content}>
        <div style={styles.mainLayout}>
          {/* Cart Section - full height with scroll */}
          <div style={styles.cartContainer}>
            <h2 style={{ 
              color: '#2c3e50', 
              marginBottom: '20px', 
              fontSize: '18px',
              textAlign: 'center' 
            }}>
              SELECTED DRUGS
            </h2>

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Drug</th>
                  <th style={styles.tableHeader}>Qty</th>
                  <th style={styles.tableHeader}>Price</th>
                  <th style={styles.tableHeader}>Amount</th>
                  <th style={styles.tableHeader}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sellItems.map((item, index) => (
                  <tr key={index}>
                    <td style={styles.tableCell}>{item.drug} {item.packaging}</td>
                    <td style={styles.tableCell}>
                      <input
                        type="number"
                        value={item.quantity}
                      onChange={(e) => {
  const value = e.target.value;
  handleQuantityChange(index, value);
}}
                        style={styles.inputField}
                        min="1"
                        onWheel={(e) => e.preventDefault()}
                      />
                    </td>
                    <td style={styles.tableCell}>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                        style={styles.inputField}
                        min="0"
                        step="100"
                        onWheel={(e) => e.preventDefault()}
                      />
                    </td>
                    <td style={styles.tableCell}>UGX {Math.floor(item.amount)}</td>
                    <td style={styles.tableCell}>
                      <button 
                        onClick={() => handleRemoveSellItem(index)} 
                        style={styles.removeButton}
                        title="Remove item"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))}
                {selectedDrug && (
                  <tr>
                    <td style={styles.tableCell}>{selectedDrug.Drug} {selectedDrug.Packaging}</td>
                    <td style={styles.tableCell}>
                      <input
                        type="number"
                        value={quantityToSell}
                       onChange={(e) => {
  const value = e.target.value;
  setQuantityToSell(value === '' ? '' : Math.max(1, Number(value)));
}}
                        style={styles.inputField}
                        min="1"
                        onWheel={(e) => e.preventDefault()}
                      />
                    </td>
                    <td style={styles.tableCell}>
                      <input
                        type="number"
                        value={selectedDrug.Selling_Price || ''}
                        onChange={(e) => setSelectedDrug({
                          ...selectedDrug,
                          Selling_Price: Math.max(0, Number(e.target.value))
                        })}
                        style={styles.inputField}
                        min="0"
                        step="100"
                        onWheel={(e) => e.preventDefault()}
                      />
                    </td>
                    <td style={styles.tableCell}>
                      UGX {Math.floor(quantityToSell * (selectedDrug.Selling_Price || 0))}
                    </td>
                    <td style={styles.tableCell}>
                      <button 
                        onClick={handleSellButtonClick} 
                        style={styles.addButton}
                        disabled={!selectedDrug}
                        title="Add to cart"
                      >
                        <FontAwesomeIcon icon={faShoppingCart} /> Add
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginTop: 'auto',
              paddingTop: '15px',
              borderTop: '1px solid #eee'
            }}>
              <div style={styles.totalAmount}>Total: UGX {Math.floor(totalAmount)}</div>
              <button
                onClick={handleConfirmTransaction}
                disabled={!isSellButtonActive || confirmClicked}
                style={{
                  ...styles.confirmButton,
                  opacity: isSellButtonActive ? 1 : 0.6,
                  cursor: isSellButtonActive ? 'pointer' : 'not-allowed'
                }}
              >
                <FontAwesomeIcon icon={faCheck} /> {confirmButtonText}
              </button>
            </div>
          </div>

          {/* Drug List Section - full height with scroll */}
          <div style={styles.drugListContainer}>
            <div style={{ position: 'relative' }}>
              <FontAwesomeIcon 
                icon={faSearch} 
                style={styles.searchIcon}
              />
              <input
                style={styles.searchInput}
                type="text"
                placeholder="Search drugs... Click to select"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div style={styles.drugListContent}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Drug</th>
                    <th style={styles.tableHeader}>Available</th>
                    <th style={styles.tableHeader}>Packaging</th>
                    <th style={styles.tableHeader}>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {stockData
                    .filter(drug => drug.Drug.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((drug, index) => (
                      <tr 
                        key={index} 
                        onClick={() => handleDrugSelect(drug)}
                        style={{
                          ...styles.drugRow,
                          backgroundColor: selectedDrug?.Drug === drug.Drug ? '#f0f7ff' : 'transparent'
                        }}
                      >
                        <td style={styles.tableCell}>{drug.Drug}</td>
                        <td style={styles.tableCell}>{drug.Quantity}</td>
                        <td style={styles.tableCell}>{drug.Packaging}</td>
                        <td style={styles.tableCell}>{drug.Selling_Price}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Transaction Prompt Modal */}
        {showTransactionPrompt && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalContent}>
              <h2 style={{ color: '#2c3e50', marginBottom: '20px' }}>Confirm Transaction</h2>
              
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ marginBottom: '10px' }}>Items to Sell:</h3>
                {sellItems.map((item, index) => (
                  <div key={index} style={{ marginBottom: '8px' }}>
                    {item.quantity} {item.packaging} of {item.drug} - UGX {item.amount}
                  </div>
                ))}
              </div>
              
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: '18px', 
                margin: '15px 0',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                Total Amount: UGX {Math.floor(totalAmount)}
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                Served by: <strong>{employeeName}</strong>
              </div>
              
              {responseMessage && (
                <div style={responseMessage.type === "success-message" ? styles.successMessage : styles.errorMessage}>
                  {responseMessage.text}
                </div>
              )}
              
              <div style={styles.transactionButtons}>
                <button onClick={handleCancelTransaction} style={styles.cancelButton}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button 
                  onClick={handleConfirmSale} 
                  disabled={confirmClicked}
                  style={{
                    ...styles.confirmButton,
                    opacity: confirmClicked ? 0.7 : 1,
                    cursor: confirmClicked ? 'not-allowed' : 'pointer'
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} /> {confirmButtonText}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Receipt Modal */}
        {showReceiptModal && (
          <ReceiptModal
            receiptDetails={receiptDetails}
            onClose={() => setShowReceiptModal(false)}
          />
        )}
      </div>

      {/* Bottom Navigation */}
      <div style={styles.bottomNav}>
        <Link to={`/remove-drugs/?token=${tokenFromUrl}`} style={styles.navButton}>
          Non-Sale Stock Removal
        </Link>
        <Link to={`/dispensed-and-sold/?token=${tokenFromUrl}`} style={styles.navButton}>
          Sold Drugs Overview
        </Link>
        <Link to={`/removed-drugs-equipment/?token=${tokenFromUrl}`} style={styles.navButton}>
          Removed Drugs Overview
        </Link>
      </div>
    </div>
  );
}

export default Selldrugs;